import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import Navigation from "@/components/Navigation";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/lib/supabase";
import { getApiUrl } from "@/lib/utils";

interface UserProfile {
  Id: number;
  name: string;
  email: string;
  phone: string;
  shopAddress: string;
  Orders: number;
  password: string;
  isActive: boolean;
}

const Profile = () => {
  const [profile, setProfile] = useState<UserProfile>({
    Id: 0,
    name: "",
    email: "",
    phone: "",
    shopAddress: "",
    Orders: 0,
    password: "",
    isActive: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();
  const userId = localStorage.getItem("clientId");

  // Get environment variables
  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;
  const apiToken = import.meta.env.VITE_API_TOKEN;
  const clientsTable = import.meta.env.VITE_CLIENTS_TABLE;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Check if environment variables are available
        if (!apiBaseUrl || !apiToken || !clientsTable) {
          console.error("Profile: Missing required environment variables", {
            hasApiBaseUrl: !!apiBaseUrl,
            hasApiToken: !!apiToken,
            hasClientsTable: !!clientsTable
          });
          toast({
            title: "Configuration Error",
            description: "Application is not properly configured. Please contact support.",
            variant: "destructive",
          });
          return;
        }

        // Get properly formatted API URL
        const apiUrl = getApiUrl(apiBaseUrl);

        // Get clientId from localStorage
        const clientId = localStorage.getItem("clientId");
        
        // Get Supabase user information as backup
        const { data: { user } } = await supabase.auth.getUser();
        const userEmail = user?.email;
        
        console.log("Profile: Fetching profile data", { 
          hasClientId: !!clientId, 
          hasSupabaseUser: !!user,
          userEmail 
        });
        
        const response = await fetch(
          `${apiUrl}/tables/${clientsTable}/records`,
          {
            headers: {
              accept: "application/json",
              "xc-token": apiToken,
              "User-Agent": "wedding-orderly-client"
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch profile");
        }

        const data = await response.json();
        
        // First try to find by clientId
        let userProfile = clientId ? data.list.find((user: any) => user.Id.toString() === clientId) : null;
        
        // If not found by clientId but we have Supabase user, try to find by supabaseUserId or email
        if (!userProfile && user) {
          console.log("Profile: Client ID not found, searching by Supabase user ID or email");
          
          // Try to find by Supabase user ID
          userProfile = data.list.find((record: any) => record.supabaseUserId === user.id);
          
          // If still not found, try by email
          if (!userProfile && userEmail) {
            console.log("Profile: Searching by email");
            userProfile = data.list.find((record: any) => record.email === userEmail);
            
            // If found by email but supabaseUserId is missing, we should update it
            if (userProfile && !userProfile.supabaseUserId) {
              console.log("Profile: Found by email, updating supabaseUserId");
              try {
                await fetch(
                  `${apiUrl}/tables/${clientsTable}/records/${userProfile.Id}`,
                  {
                    method: "PATCH",
                    headers: {
                      "Content-Type": "application/json",
                      "xc-token": apiToken,
                      "User-Agent": "wedding-orderly-client"
                    },
                    body: JSON.stringify({
                      supabaseUserId: user.id
                    }),
                  }
                );
                
                // Update localStorage with the correct client ID
                localStorage.setItem("clientId", userProfile.Id.toString());
                console.log("Profile: Updated supabaseUserId and localStorage clientId");
              } catch (updateError) {
                console.error("Profile: Failed to update supabaseUserId", updateError);
              }
            }
          }
        }
        
        if (userProfile) {
          console.log("Profile: User profile found", { profileId: userProfile.Id });
          
          // Update localStorage with the correct client ID if it doesn't match
          if (clientId !== userProfile.Id.toString()) {
            localStorage.setItem("clientId", userProfile.Id.toString());
            console.log("Profile: Updated localStorage clientId");
          }
          
          setProfile({
            Id: userProfile.Id,
            email: userProfile.email || "",
            name: userProfile.name || "",
            phone: userProfile.phone || "",
            shopAddress: userProfile.shopAddress || "",
            Orders: userProfile.Orders || 0,
            password: userProfile.password || "",
            isActive: userProfile.isActive !== false
          });
        } else {
          console.error("Profile: No matching user profile found");
          toast({
            title: "Error",
            description: "No profile found. Please contact support.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Profile: Error fetching profile", error);
        toast({
          title: "Error",
          description: "Failed to load profile data",
          variant: "destructive",
        });
      }
    };

    fetchProfile();
  }, [userId, toast, apiBaseUrl, apiToken, clientsTable]);

  const handleSave = async () => {
    try {
      // Check if environment variables are available
      if (!apiBaseUrl || !apiToken || !clientsTable) {
        console.error("Profile: Missing required environment variables for update");
        toast({
          title: "Configuration Error",
          description: "Application is not properly configured. Please contact support.",
          variant: "destructive",
        });
        return;
      }

      // Get properly formatted API URL
      const apiUrl = getApiUrl(apiBaseUrl);
      
      console.log("Profile: Updating profile with ID:", profile.Id);
      
      // Use PATCH on the collection endpoint, not on a specific record
      const response = await fetch(
        `${apiUrl}/tables/${clientsTable}/records`,
        {
          method: "PATCH",
          headers: {
            "accept": "application/json",
            "Content-Type": "application/json",
            "xc-token": apiToken,
            "User-Agent": "wedding-orderly-client"
          },
          body: JSON.stringify({
            Id: profile.Id, // Include the ID in the body to identify which record to update
            name: profile.name,
            email: profile.email,
            phone: profile.phone,
            shopAddress: profile.shopAddress,
            isActive: profile.isActive
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Profile update failed:", {
          status: response.status,
          text: errorText
        });
        throw new Error("Failed to update profile");
      }

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Profile update error:", error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navigation />
      <div className="container mx-auto py-8 px-4">
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <Input
                value={profile.email}
                onChange={(e) =>
                  setProfile({ ...profile, email: e.target.value })
                }
                disabled={!isEditing}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Store Name</label>
              <Input
                value={profile.name}
                onChange={(e) =>
                  setProfile({ ...profile, name: e.target.value })
                }
                disabled={!isEditing}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Phone</label>
              <Input
                value={profile.phone}
                onChange={(e) =>
                  setProfile({ ...profile, phone: e.target.value })
                }
                disabled={!isEditing}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Store Address (including post code)</label>
              <Textarea
                value={profile.shopAddress}
                onChange={(e) =>
                  setProfile({ ...profile, shopAddress: e.target.value })
                }
                placeholder="Enter your complete store address including street, building number, suburb, city, state and post code"
                disabled={!isEditing}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Total Orders</label>
              <Input
                value={profile.Orders}
                disabled={true}
                readOnly
              />
            </div>
            {isEditing && (
              <div className="flex items-center space-x-2">
                <Switch
                  checked={profile.isActive}
                  onCheckedChange={(checked) =>
                    setProfile({ ...profile, isActive: checked })
                  }
                  id="active-status"
                />
                <Label htmlFor="active-status">Account Active</Label>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              {isEditing ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave}>Save</Button>
                </>
              ) : (
                <Button onClick={() => setIsEditing(true)}>Edit</Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Profile;
